import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Calendar, Crown, CreditCard, Key, Layers, Plus, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';
import { AdminConfirmDialog } from '../../components/AdminUi';

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extendTarget, setExtendTarget] = useState(null);

  useEffect(() => { fetchUser(); }, [id]); // eslint-disable-line

  const fetchUser = async () => {
    setLoading(true);
    try {
      setData(await apiGet(`${API_ROUTES.admin.users}/${id}`));
    } catch {
      toast.error('Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const extendSubscription = async () => {
    if (!extendTarget) return;
    try {
      const res = await apiFetch(`${API_ROUTES.admin.users}/${id}/extend-subscription`, {
        method: 'POST',
        body: { days: extendTarget, planSlug: activePlanSlug },
      });
      if (!res.ok) throw new Error();
      toast.success(`Extended +${extendTarget}d`);
      setExtendTarget(null);
      fetchUser();
    } catch {
      toast.error('Failed to extend');
    }
  };

  const togglePlan = async (newPlan) => {
    try {
      await apiFetch(`${API_ROUTES.admin.users}/${id}/plan`, {
        method: 'PATCH',
        body: { plan: newPlan },
      });
      toast.success(`Plan changed to ${newPlan}`);
      fetchUser();
    } catch {
      toast.error('Failed');
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">User not found</div>;

  const { user, accounts, apiKeys, subscriptions, payments } = data;
  const activePlanSlug =
    subscriptions?.find((subscription) => subscription.status === 'active')?.planSlug ||
    user.plan ||
    'pro';

  return (
    <div className="max-w-5xl">
      <button onClick={() => navigate('/admin/users')} className="flex items-center gap-2 text-gray-500 hover:text-gray-300 mb-4 text-sm">
        <ArrowLeft size={16} /> Back to Users
      </button>

      {/* User header */}
      <div className="bg-dark-card border border-dark-border rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <User size={24} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-xl font-bold text-gray-100">{user.username}</h1>
                <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${user.plan === 'free' ? 'bg-gray-700 text-gray-400' : 'bg-amber-500/20 text-amber-400'}`}>{user.plan}</span>
                {user.role === 'admin' && <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">ADMIN</span>}
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="flex items-center gap-1"><Mail size={13} /> {user.email}</span>
                <span className="flex items-center gap-1"><Calendar size={13} /> Joined {user.createdAt?.split('T')[0]}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user.plan === 'free' ? (
              <button onClick={() => togglePlan('pro')} className="bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-lg text-sm hover:bg-amber-500/30">
                <Crown size={14} className="inline mr-1" /> Upgrade to Pro
              </button>
            ) : (
              <button onClick={() => togglePlan('free')} className="bg-gray-700/50 text-gray-300 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-700">
                Downgrade
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-dark-card border border-dark-border rounded-xl p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-3">Subscription Actions</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setExtendTarget(7)} className="bg-emerald-500/10 text-emerald-400 px-3 py-1.5 rounded-lg text-xs hover:bg-emerald-500/20"><Plus size={12} className="inline" /> 7 days</button>
          <button onClick={() => setExtendTarget(30)} className="bg-emerald-500/10 text-emerald-400 px-3 py-1.5 rounded-lg text-xs hover:bg-emerald-500/20"><Plus size={12} className="inline" /> 30 days</button>
          <button onClick={() => setExtendTarget(90)} className="bg-emerald-500/10 text-emerald-400 px-3 py-1.5 rounded-lg text-xs hover:bg-emerald-500/20"><Plus size={12} className="inline" /> 90 days</button>
          <button onClick={() => setExtendTarget(365)} className="bg-emerald-500/10 text-emerald-400 px-3 py-1.5 rounded-lg text-xs hover:bg-emerald-500/20"><Plus size={12} className="inline" /> 1 year</button>
        </div>
      </div>

      {/* Subscriptions */}
      <div className="bg-dark-card border border-dark-border rounded-xl p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2"><RefreshCw size={14} /> Subscriptions ({subscriptions?.length || 0})</h3>
        {!subscriptions?.length ? (
          <p className="text-gray-500 text-sm">No subscriptions yet</p>
        ) : (
          <div className="space-y-2">
            {subscriptions.map((s) => (
              <div key={s.id} className="flex items-center justify-between bg-dark-surface rounded-lg px-4 py-2.5 text-sm">
                <div>
                  <span className="text-gray-200 capitalize">{s.planSlug}</span>
                  <span className="text-gray-500 ml-2">- {s.billingCycle}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 text-xs">Expires: {s.expiresAt?.split('T')[0]}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${s.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>{s.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment history */}
      <div className="bg-dark-card border border-dark-border rounded-xl p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2"><CreditCard size={14} /> Payment History ({payments?.length || 0})</h3>
        {!payments?.length ? (
          <p className="text-gray-500 text-sm">No payments yet</p>
        ) : (
          <div className="space-y-2">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-dark-surface rounded-lg px-4 py-2.5 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-gray-200 capitalize">{p.planSlug}</span>
                  <span className="text-gray-500">- {p.billingCycle}</span>
                  <span className="text-gray-300">${p.amountUsd}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 text-xs">{p.createdAt?.split('T')[0]}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${p.status === 'approved' ? 'bg-green-500/20 text-green-400' : p.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>{p.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Accounts + API keys */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-dark-card border border-dark-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2"><Layers size={14} /> Accounts ({accounts?.length || 0})</h3>
          {!accounts?.length ? <p className="text-gray-500 text-sm">No accounts</p> : (
            <ul className="space-y-1">
              {accounts.map((a) => (
                <li key={a.id} className="text-xs text-gray-400 flex justify-between">
                  <span>#{a.accountNumber} ({a.broker || 'unknown'})</span>
                  <span className="text-gray-600">{a.tradeCount} trades</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-dark-card border border-dark-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2"><Key size={14} /> API Keys ({apiKeys?.length || 0})</h3>
          {!apiKeys?.length ? <p className="text-gray-500 text-sm">No keys</p> : (
            <ul className="space-y-1">
              {apiKeys.map((k) => (
                <li key={k.id} className="text-xs text-gray-400 flex justify-between">
                  <span>{k.name}</span>
                  <span className={k.isActive ? 'text-green-400' : 'text-gray-600'}>{k.totalRequests} reqs</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <AdminConfirmDialog
        open={!!extendTarget}
        title="Extend Subscription"
        message={`Extend this user's ${activePlanSlug} subscription by ${extendTarget} days?`}
        confirmLabel="Extend"
        tone="success"
        onConfirm={extendSubscription}
        onCancel={() => setExtendTarget(null)}
      />
    </div>
  );
}

